/* product-pack.js - the add of blocks/product-pack.liquid (see the reasoning
   there). One press posts the pack's variant to /cart/add.js with the drawer
   section, paints and opens the drawer, and dispatches, once per press:

     pp:pack:add { pack_variant_id, result }   result: 'ok' | 'refused' | 'error'

   'refused' is Shopify's 422: it cannot tell a sold out pack from the stock
   cap, so no more is claimed. Anything else that fails is 'error'. The event
   leaves from the button, inside the data-pp="pack-upsell" root (this
   element) that pp-ux listens on.

   The press stays busy until the drawer is open (its module may load on this
   very add), so a second press cannot post a second pack meanwhile. When the
   drawer cannot open (its module does not load, or loads without an opener)
   the pack is in the bag all the same: the status line under the card says it
   was added (data-str-added), and the result stays 'ok'.

   The card is a plain form that posts to /cart/add with no script; this is the
   enhancement. No selling plan is ever sent: a bundle cannot carry one. The
   shopper reads Shopify's own description of a refusal, or the locale string,
   never an exception message. */
(() => {
  if (customElements.get('pp-product-pack')) return;

  const root = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
  const pp = () => window.PP || {};

  class ProductPack extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.form = this.querySelector('[data-pack-form]');
      if (this.form) this.form.addEventListener('submit', (event) => this.add(event));
    }

    drawer() {
      return document.querySelector('[data-cart-drawer]');
    }

    drawerSection() {
      const drawer = this.drawer();
      return (drawer && drawer.getAttribute('data-section-id')) || '';
    }

    async add(event) {
      event.preventDefault();
      if (this.busy) return;
      const button = this.querySelector('[data-pack-add]');
      const error = this.querySelector('[data-pack-error]');
      const status = this.querySelector('[data-pack-status]');
      const id = Number(this.dataset.packVariant);
      this.busy = true;
      /* aria-disabled, not disabled: a disabled button drops the focus, and a
         refusal must leave the shopper on the button, next to its message. A
         second press in flight is refused by `busy`. */
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      error.hidden = true;
      error.textContent = '';
      if (status) {
        status.hidden = true;
        status.textContent = '';
      }
      let result = 'error';
      let failed = false;
      try {
        const body = { items: [{ id, quantity: 1 }] };
        const section = this.drawerSection();
        if (section) {
          body.sections = section;
          body.sections_url = window.location.pathname;
        }
        const res = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok || !payload) {
          if (res.status === 422) result = 'refused';
          const refusal = new Error('product-pack');
          refusal.shopperMessage = (payload && payload.description) || '';
          throw refusal;
        }
        result = 'ok';
        if (pp().publish && pp().EVENTS) pp().publish(pp().EVENTS.cartUpdate, { source: 'product-pack' });
        const opened = await this.openDrawer((section && payload.sections && payload.sections[section]) || null);
        /* Unhide BEFORE writing, both live regions: one mutated while still
           hidden is a mutation several screen readers never announce (same
           order as recoError() in assets/cart-drawer.js). */
        if (!opened && status) {
          const added = this.getAttribute('data-str-added') || '';
          status.hidden = !added;
          status.textContent = added;
        }
      } catch (e) {
        const message = (e && e.shopperMessage) || this.getAttribute('data-str-error') || '';
        error.hidden = !message;
        error.textContent = message;
        failed = true;
        if (pp().publish && pp().EVENTS) pp().publish(pp().EVENTS.cartError, { source: 'product-pack' });
      } finally {
        this.busy = false;
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        if (failed) button.focus({ preventScroll: true });
        button.dispatchEvent(new CustomEvent('pp:pack:add', { bubbles: true, detail: { pack_variant_id: id, result } }));
      }
    }

    /* Paint before open, as assets/paris-box.js does: the drawer module may be
       loaded by this very add, too late to hear the update it was loaded for.
       Resolves false when the drawer could not be shown (the module did not
       load, loaded without an opener, or painting it threw), true otherwise;
       never rejects. */
    openDrawer(html) {
      const show = () => {
        try {
          if (html && typeof pp().paintCartSection === 'function') pp().paintCartSection(html);
          if (pp().openCartDrawer) pp().openCartDrawer();
          return !!pp().openCartDrawer;
        } catch (e) {
          return false;
        }
      };
      if (pp().openCartDrawer) return Promise.resolve(show());
      const drawer = this.drawer();
      const src = drawer && drawer.getAttribute('data-cart-drawer-js');
      if (src && pp().loadModule) return pp().loadModule(src).then(show, () => false);
      window.location.assign(root() + 'cart');
      return Promise.resolve(true);
    }
  }

  customElements.define('pp-product-pack', ProductPack);
})();
